import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Building2, ChevronDown, Check, Plus, Users, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LS_ORG = "aevum_active_org_v1";

export default function OrgSwitcher() {
  const [orgs, setOrgs] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(LS_ORG));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // holds org obj
  const [deleting, setDeleting] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/orgs");
      setOrgs(r.data);
      // If active id is stale or unset, default to personal
      if (!r.data.find((o) => o.id === activeId)) {
        const personal = r.data.find((o) => o.personal) || r.data[0];
        if (personal) {
          setActiveId(personal.id);
          localStorage.setItem(LS_ORG, personal.id);
        }
      }
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const switchTo = (org) => {
    if (org.id === activeId) return;
    localStorage.setItem(LS_ORG, org.id);
    toast.success(`Switched to ${org.name}`);
    // Hard reload so every data query refetches under the new workspace scope.
    setTimeout(() => window.location.reload(), 250);
  };

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/orgs", { name: name.trim() });
      toast.success(`${r.data.name} created`);
      setName(""); setDialogOpen(false);
      localStorage.setItem(LS_ORG, r.data.id);
      setTimeout(() => window.location.reload(), 250);
    } catch { toast.error("Failed to create workspace"); }
    finally { setSaving(false); }
  };

  const deleteOrg = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/orgs/${confirmDelete.id}`);
      toast.success(`${confirmDelete.name} deleted`);
      // If we just deleted the active workspace, fall back to personal.
      if (confirmDelete.id === activeId) {
        const personal = orgs.find((o) => o.personal);
        if (personal) localStorage.setItem(LS_ORG, personal.id);
        else localStorage.removeItem(LS_ORG);
      }
      setConfirmDelete(null);
      setTimeout(() => window.location.reload(), 250);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete workspace");
    } finally { setDeleting(false); }
  };

  const active = orgs.find((o) => o.id === activeId) || orgs[0];
  if (!active) return null;

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid="org-switcher"
            className="inline-flex items-center gap-2 bg-surface-container rounded-full px-3 py-2 hover:bg-primary/8 transition-colors duration-200 ease-md max-w-[220px]"
          >
            <div className={cn("w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium shrink-0",
              active.personal ? "bg-primary-container text-primary-onContainer" : "bg-tertiary-container text-tertiary-onContainer")}>
              {active.name.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm font-medium text-surface-on truncate">{active.name}</span>
            <ChevronDown className="w-3.5 h-3.5 text-surface-onVariant shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-1 bg-surface rounded-2xl border border-outline-variant shadow-md">
          <div className="px-3 py-2 text-[10px] tracking-widest uppercase text-surface-onVariant">Workspaces</div>
          {orgs.map((o) => (
            <div
              key={o.id}
              className="group w-full flex items-center gap-1 rounded-xl hover:bg-primary/8 transition-colors duration-200 ease-md"
            >
              <button
                data-testid={`org-item-${o.id}`}
                onClick={() => switchTo(o)}
                className="flex-1 text-left px-3 py-2 rounded-xl flex items-center gap-3 min-w-0"
              >
                <div className={cn("w-7 h-7 rounded-md flex items-center justify-center text-xs font-medium shrink-0",
                  o.personal ? "bg-primary-container text-primary-onContainer" : "bg-tertiary-container text-tertiary-onContainer")}>
                  {o.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-surface-on truncate">{o.name}</div>
                  <div className="text-[10px] text-surface-onVariant capitalize">{o.role}{o.personal ? " · personal" : ""}</div>
                </div>
                {o.id === activeId && <Check className="w-4 h-4 text-primary shrink-0" />}
              </button>
              {!o.personal && o.role === "owner" && (
                <button
                  data-testid={`org-delete-${o.id}`}
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(o); }}
                  title="Delete workspace"
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-2 rounded-full hover:bg-error/12 text-surface-onVariant hover:text-error shrink-0 mr-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <div className="h-px bg-outline-variant my-1" />
          {active && !active.personal && (
            <button
              data-testid="org-manage-members"
              onClick={() => nav("/members")}
              className="w-full text-left px-3 py-2 rounded-xl hover:bg-primary/8 transition-colors duration-200 ease-md flex items-center gap-3 text-surface-on"
            >
              <Users className="w-4 h-4 text-surface-onVariant" />
              <span className="text-sm">Manage members</span>
            </button>
          )}
          <button
            data-testid="org-create-open"
            onClick={() => setDialogOpen(true)}
            className="w-full text-left px-3 py-2 rounded-xl hover:bg-primary/8 transition-colors duration-200 ease-md flex items-center gap-3 text-primary"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">New team workspace</span>
          </button>
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-surface rounded-hero border-none p-6" data-testid="org-create-dialog">
          <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">New team workspace</DialogTitle></DialogHeader>
          <p className="text-sm text-surface-onVariant mt-1">A workspace for shared work. You'll be the owner.</p>
          <form onSubmit={create} className="mt-4 space-y-4">
            <div>
              <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">Workspace name</div>
              <div className="flex items-center bg-surface-containerHigh rounded-t-lg border-b-2 border-outline focus-within:border-primary px-3 py-2.5">
                <Building2 className="w-4 h-4 text-surface-onVariant mr-2" />
                <input
                  data-testid="org-name-input"
                  value={name} onChange={(e) => setName(e.target.value)}
                  autoFocus placeholder="e.g. Bloom & Co Retail"
                  className="bg-transparent outline-none w-full text-surface-on"
                />
              </div>
            </div>
            <button
              data-testid="org-create-save"
              type="submit" disabled={saving || !name.trim()}
              className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md"
            >{saving ? "Creating…" : "Create workspace"}</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="max-w-md bg-surface rounded-hero border-none p-6" data-testid="org-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-surface-on text-lg font-medium">
              Delete "{confirmDelete?.name}"?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-surface-onVariant mt-2">
            This permanently deletes the workspace and every item, transaction, terminal,
            settlement, journal, and habit inside it. This cannot be undone.
          </p>
          <div className="mt-5 flex gap-2 justify-end">
            <button
              data-testid="org-delete-cancel"
              onClick={() => setConfirmDelete(null)}
              className="rounded-full px-5 py-2.5 text-sm font-medium text-surface-on hover:bg-primary/8 transition-colors"
            >Cancel</button>
            <button
              data-testid="org-delete-confirm"
              onClick={deleteOrg}
              disabled={deleting}
              className="rounded-full px-5 py-2.5 text-sm font-medium bg-error text-error-onContainer hover:brightness-110 active:scale-[0.98] disabled:opacity-60 transition-[transform,filter]"
            >{deleting ? "Deleting…" : "Delete workspace"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
